import { WizardStep } from "../WizardStep";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import {
  Descriptions,
  Card,
  Table,
  Space,
  Typography,
  Button,
  Tag,
  Modal,
  Empty,
  Spin,
  message,
  Checkbox,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "../../../lib/supabase";
import { createCommercialInvoiceSignedUrl } from "../../../utils/commercialInvoiceUtils";
import { createCustomerPackingSlipSignedUrl } from "../../../utils/customerPackingSlipUtils";
import dayjs from "dayjs";
import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  isStorageOption,
  STORAGE_OPTION_TRANSLATION_KEYS,
} from "../../../utils/storageOptionUtils";
import {
  isServiceLevel,
  SERVICE_LEVEL_TRANSLATION_KEYS,
} from "../../../utils/serviceLevelUtils";
import { generateAndSaveShippingLabel } from "../../../utils/shippingLabel";
import { getCustomerPackingSlips } from "../../../services/customerPackingSlipService";
import { fetchAgents } from "../../../services/agentService";
import { updateBookingAgent } from "../../../services/bookingService";
import { saveBookingQuote } from "../../../services/bookingQuoteService";
import { useTemplatePricing } from "../hooks/useTemplatePricing";
import { usePricingCalculation } from "../hooks/usePricingCalculation";
import { useFeatureFlags } from "../../../contexts/FeatureFlagContext";

const FENEX_CONDITIONS_URL =
  "https://www.tln.nl/ledenvoordeel/voorwaarden#nederlandse-expeditievoorwaarden";

interface BookingDetails {
  transport_type_name?: string;
  transport_type_abbreviation?: string;
  booking_type_name?: string;
  incoterm_name?: string;
  supplier_name?: string;
  supplier_details?: {
    email: string;
    phone: string;
    wechat: string;
    country: string;
    province: string;
    city: string;
    address: string;
  };
  company_name?: string;
}

export interface ReviewDetailsStepProps {
  prevStepOverride?: number;
  onPreviousOverride?: () => void | Promise<void>;
  useV2Navigation?: boolean;
}

export const ReviewDetails = ({
  onPreviousOverride,
}: ReviewDetailsStepProps = {}) => {
  const { state, updateStep } = useBookingWizard();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [details, setDetails] = useState<BookingDetails>({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingLabels, setIsGeneratingLabels] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [acceptedFenexConditions, setAcceptedFenexConditions] = useState(false);
  const [customerPackingSlipPath, setCustomerPackingSlipPath] = useState<
    string | null
  >(null);

  // Get pricing calculator feature flag
  const { isPricingCalculatorEnabled } = useFeatureFlags();

  // Get pricing data (only call hooks if pricing calculator is enabled)
  const templatePricing = useTemplatePricing();
  const pricingCalculation = usePricingCalculation();
  
  // Only use pricing data if feature flag is enabled
  const pricingResult = isPricingCalculatorEnabled ? templatePricing.pricingResult : null;
  const calculatedPrices = isPricingCalculatorEnabled ? pricingCalculation.calculatedPrices : null;

  // Add debugging to check if products exist in the state
  useEffect(() => {
    console.log("ReviewDetails - Current state:", state);

    // Check if products exist in state
    if (state.products && state.products.length > 0) {
      console.log(
        "ReviewDetails - Products from state:",
        state.products.length,
        "products"
      );
    } else {
      console.log(
        "ReviewDetails - No products in state, checking localStorage"
      );

      // Try to get products from localStorage as a fallback
      const tempProducts = localStorage.getItem("tempProducts");
      if (tempProducts) {
        try {
          const parsedProducts = JSON.parse(tempProducts);
          console.log(
            "ReviewDetails - Found products in localStorage:",
            parsedProducts.length
          );

          // Update the state with products from localStorage
          updateStep(state.currentStep, { products: parsedProducts });
        } catch (error) {
          console.error("Error parsing products from localStorage:", error);
        }
      } else {
        console.log("ReviewDetails - No products found in localStorage either");
      }
    }
  }, [state, updateStep]);

  // Fetch customer packing slip from state or database
  useEffect(() => {
    const fetchPackingSlip = async () => {
      if (state.customer_packing_slip_file) {
        setCustomerPackingSlipPath(state.customer_packing_slip_file);
        return;
      }

      if (!state.id) {
        setCustomerPackingSlipPath(null);
        return;
      }

      try {
        const result = await getCustomerPackingSlips(state.id);
        if (result.success && result.data.length > 0) {
          const path = result.data[0].booking_packing_slip_file;
          setCustomerPackingSlipPath(path);
        } else {
          setCustomerPackingSlipPath(null);
        }
      } catch (error) {
        console.error("ReviewDetails - Error fetching packing slip:", error);
        setCustomerPackingSlipPath(null);
      }
    };

    fetchPackingSlip();
  }, [state.id, state.customer_packing_slip_file]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const detailsObj: BookingDetails = {};

        // Fetch transport type
        if (state.transport_type) {
          const { data: transportType } = await supabase
            .from("booking_transport_types")
            .select("name")
            .eq("id", state.transport_type)
            .single();
          if (transportType) {
            detailsObj.transport_type_name = transportType.name;
          }
        }

        // Fetch transport type abbreviation
        if (state.transport_type) {
          const { data: transportTypeAbbreviation } = await supabase
            .from("booking_transport_types")
            .select("abbreviation")
            .eq("id", state.transport_type)
            .single();
          if (transportTypeAbbreviation) {
            detailsObj.transport_type_abbreviation =
              transportTypeAbbreviation.abbreviation;
          }
        }

        // Fetch booking type
        if (state.booking_type) {
          const { data: bookingType } = await supabase
            .from("bookings_type")
            .select("name")
            .eq("id", state.booking_type)
            .single();
          if (bookingType) {
            detailsObj.booking_type_name = bookingType.name;
          }
        }

        // Fetch incoterm
        if (state.booking_incoterm) {
          const { data: incoterm } = await supabase
            .from("bookings_incoterm")
            .select("name")
            .eq("id", state.booking_incoterm)
            .single();
          if (incoterm) {
            detailsObj.incoterm_name = incoterm.name;
          }
        }

        // Fetch supplier with all details
        if (state.supplier_id) {
          const { data: supplier } = await supabase
            .from("booking_supplier")
            .select("*")
            .eq("id", state.supplier_id)
            .single();
          if (supplier) {
            detailsObj.supplier_name = supplier.name;
            detailsObj.supplier_details = {
              email: supplier.email,
              phone: supplier.phone,
              wechat: supplier.wechat,
              country: supplier.country,
              province: supplier.province,
              city: supplier.city,
              address: supplier.address,
            };
          }
        }

        // Company name from DB (authoritative for this booking) — avoids race with company context on submit
        if (state.company_id) {
          const { data: companyRow, error: companyError } = await supabase
            .from("company")
            .select("name")
            .eq("id", state.company_id)
            .single();
          if (companyError) {
            console.error("Error fetching company name for review:", companyError);
          }
          detailsObj.company_name =
            companyRow?.name?.trim() || "Your Company";
        } else {
          detailsObj.company_name = "Your Company";
        }

        setDetails(detailsObj);
      } catch (error) {
        console.error("Error fetching details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [
    state.transport_type,
    state.booking_type,
    state.booking_incoterm,
    state.supplier_id,
    state.company_id,
  ]);

  const showConfirmModal = () => {
    if (!acceptedFenexConditions) {
      message.warning(
        t("bookingWizard.reviewDetails.acceptFenexConditionsRequired")
      );
      return;
    }
    setIsConfirmModalVisible(true);
  };

  const handleCancel = () => {
    setIsConfirmModalVisible(false);
  };

  const handleConfirmSubmit = () => {
    setIsConfirmModalVisible(false);
    handleSubmit();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setIsGeneratingLabels(true);

    try {
      let companyNameForLabels = details.company_name?.trim() || "Your Company";
      if (state.company_id) {
        const { data: companyRow } = await supabase
          .from("company")
          .select("name")
          .eq("id", state.company_id)
          .single();
        if (companyRow?.name?.trim()) {
          companyNameForLabels = companyRow.name.trim();
        }
      }

      // Generate shipping labels for each product
      for (const product of state.products || []) {
        const shippingLabelPath = await generateAndSaveShippingLabel(
          {
            id: state.id!,
            reference: state.reference || "",
            transport_type: {
              name: details.transport_type_name || "",
              abbreviation: details.transport_type_abbreviation || "",
            },
            booking_incoterm: { name: details.incoterm_name || "" },
            supplier: {
              name: details.supplier_name || "",
              email: details.supplier_details?.email || "",
              phone: details.supplier_details?.phone || "",
              country: details.supplier_details?.country || "",
              city: details.supplier_details?.city || "",
              address: details.supplier_details?.address || "",
            },
          },
          {
            id: product.id!,
            name: product.name,
            hs_code: product.hs_code,
            gross_weight: product.gross_weight || 0,
            cbm: product.cbm || 0,
            pieces: product.pieces || null,
            cartons: product.cartons || null,
            ean: product.ean || null,
            company_id: state.company_id!,
          },
          companyNameForLabels
        );

        // Update the booking_product with the shipping label path
        if (shippingLabelPath) {
          const { error: updateError } = await supabase
            .from("booking_product")
            .update({ shipping_label: shippingLabelPath })
            .eq("id", product.id);

          if (updateError) {
            console.error("Error updating shipping label path:", updateError);
            throw updateError;
          }
        }
      }

      // Fetch available agents and assign the first one
      const agentsResult = await fetchAgents();
      if (
        agentsResult.error ||
        !agentsResult.data ||
        agentsResult.data.length === 0
      ) {
        message.error("No agents available. Please contact support.");
        return;
      }

      // Assign the first available agent
      const firstAgent = agentsResult.data[0];
      const agentResult = await updateBookingAgent(state.id!, firstAgent.id);
      if (agentResult.error) {
        message.error("Failed to assign agent automatically");
        return;
      }

      // Update booking status to approved
      await updateStep(state.currentStep, {
        status: "approved",
      });

      // Save pricing quote only if pricing calculator is enabled
      if (isPricingCalculatorEnabled && pricingResult) {
        try {
          const shouldUseTemplatePricing =
            pricingResult.matchedTemplate !== null &&
            pricingResult.components.length > 0;

          if (shouldUseTemplatePricing) {
            await saveBookingQuote({
              bookingId: state.id!,
              pricingResult: {
                components: pricingResult.components,
                totalCost: pricingResult.totalCost,
                currency: pricingResult.currency,
                matchedTemplate: pricingResult.matchedTemplate,
                matchingCriteria: pricingResult.matchingCriteria,
              },
              transportType: state.transport_type,
              bookingType: state.booking_type,
              incoterm: state.booking_incoterm,
              originPort: state.origin_port,
              destinationPort: state.destination_port,
              products: state.products,
            });
          } else if (calculatedPrices) {
            await saveBookingQuote({
              bookingId: state.id!,
              calculatedPrices,
              transportType: state.transport_type,
              bookingType: state.booking_type,
              incoterm: state.booking_incoterm,
              originPort: state.origin_port,
              destinationPort: state.destination_port,
              products: state.products,
            });
          }
        } catch (quoteError) {
          // Don't block booking submission if quote save fails
          console.error("Error saving quote:", quoteError);
        }
      }

      // Close the loading modal
      setIsGeneratingLabels(false);

      // Show success modal
      Modal.success({
        title: t("bookingWizard.reviewDetails.bookingSubmitted"),
        content: t("bookingWizard.reviewDetails.bookingSubmittedMessage"),
        onOk: () => navigate("/bookings"),
      });
    } catch (error) {
      console.error("Error submitting booking:", error);
      message.error(t("bookingWizard.reviewDetails.submitError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (state.commercial_invoice_file) {
      const url = await createCommercialInvoiceSignedUrl(
        state.commercial_invoice_file,
        60,
        state.id
      );
      if (!url) {
        console.error("Error generating download link");
        return;
      }
      window.open(url, "_blank");
    }
  };

  const handleDownloadPackingSlip = async () => {
    if (customerPackingSlipPath) {
      const url = await createCustomerPackingSlipSignedUrl(
        customerPackingSlipPath,
        { expiresIn: 60, bookingId: state.id }
      );
      if (!url) {
        console.error("Error generating packing slip download link");
        return;
      }
      window.open(url, "_blank");
    }
  };

  // Calculate product totals
  const calculateTotals = () => {
    if (!state.products || state.products.length === 0) {
      console.log("No products found in state for totals calculation");
      return {
        totalWeight: 0,
        totalCbm: 0,
        totalValue: 0,
        totalItems: 0,
        totalPieces: 0,
        totalCartons: 0,
      };
    }

    console.log(
      "Calculating totals for products:",
      state.products.length,
      "products"
    );
    return state.products.reduce(
      (acc, product) => ({
        totalWeight: acc.totalWeight + (product.gross_weight || 0),
        totalCbm: Number((acc.totalCbm + (product.cbm || 0)).toFixed(3)),
        totalValue: acc.totalValue + (product.value || 0),
        totalItems: acc.totalItems + 1,
        totalPieces: acc.totalPieces + (product.pieces || 0),
        totalCartons: acc.totalCartons + (product.cartons || 0),
      }),
      {
        totalWeight: 0,
        totalCbm: 0,
        totalValue: 0,
        totalItems: 0,
        totalPieces: 0,
        totalCartons: 0,
      }
    );
  };

  const totals = calculateTotals();

  if (loading) {
    return <div>Loading...</div>;
  }

  const handlePrevious = () => {
    if (onPreviousOverride) {
      onPreviousOverride();
      return;
    }
    updateStep(state.currentStep - 1, {});
  };

  return (
    <WizardStep
      onNext={showConfirmModal}
      onPrevious={handlePrevious}
      nextDisabled={isSubmitting || !acceptedFenexConditions}
      nextButtonText={t("bookingWizard.reviewDetails.submitBooking")}
      isLastStep={true}
      footerContent={
        <Checkbox
          checked={acceptedFenexConditions}
          onChange={(e) => setAcceptedFenexConditions(e.target.checked)}
        >
          {t("bookingWizard.reviewDetails.acceptFenexConditions")}{" "}
          <a
            href={FENEX_CONDITIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {t("bookingWizard.reviewDetails.acceptFenexConditionsLink")}
          </a>
        </Checkbox>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <Typography.Title level={3}>
            {t("bookingWizard.reviewDetails.title")}
          </Typography.Title>
        </div>

        <Card
          title={
            <Space>
              <InfoCircleOutlined />
              <span>{t("bookingWizard.reviewDetails.summary")}</span>
            </Space>
          }
        >
          <Descriptions bordered column={2}>
            <Descriptions.Item
              label={t("bookingWizard.reviewDetails.bookingId")}
              span={2}
            >
              {state.id || t("common.notYetAssigned")}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("bookingWizard.reviewDetails.reference")}
            >
              {state.reference || t("common.na")}
            </Descriptions.Item>
            <Descriptions.Item label={t("bookingWizard.reviewDetails.status")}>
              <Tag color={state.status === "concept" ? "gold" : "blue"}>
                {t(`status.${state.status}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item
              label={t("bookingWizard.reviewDetails.company")}
              span={2}
            >
              {details.company_name || t("common.na")}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("bookingWizard.reviewDetails.commercialInvoice")}
              span={2}
            >
              {state.commercial_invoice_file ? (
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadInvoice}
                >
                  {t("bookingWizard.reviewDetails.downloadInvoice")}
                </Button>
              ) : (
                t("common.na")
              )}
            </Descriptions.Item>
            {customerPackingSlipPath && (
              <Descriptions.Item
                label={t("bookingWizard.basicDetails.customerPackingSlip")}
                span={2}
              >
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadPackingSlip}
                >
                  {customerPackingSlipPath.split("/").pop() ||
                    t("bookingWizard.reviewDetails.downloadPackingSlip")}
                </Button>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        <Card title={t("bookingWizard.reviewDetails.transportDetails")}>
          <Descriptions bordered column={2}>
            <Descriptions.Item
              label={t("bookingWizard.reviewDetails.transportType")}
            >
              {details.transport_type_name || t("common.na")}
            </Descriptions.Item>
            {details.booking_type_name && (
              <Descriptions.Item
                label={t("bookingWizard.reviewDetails.bookingType")}
              >
                {details.booking_type_name}
              </Descriptions.Item>
            )}
            {state.service_level && isServiceLevel(state.service_level) && (
              <Descriptions.Item
                label={t("bookingWizard.reviewDetails.serviceLevel")}
              >
                {t(SERVICE_LEVEL_TRANSLATION_KEYS[state.service_level])}
              </Descriptions.Item>
            )}
            <Descriptions.Item
              label={t("bookingWizard.reviewDetails.incoterm")}
            >
              {details.incoterm_name || t("common.na")}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("bookingWizard.reviewDetails.cargoReadinessDate")}
            >
              {state.origin_pickup_date
                ? dayjs(state.origin_pickup_date).format("DD/MM/YYYY")
                : t("common.na")}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title={t("bookingWizard.reviewDetails.deliveryDetails")}>
          <Descriptions bordered column={2}>
            <Descriptions.Item
              label={t("bookingWizard.reviewDetails.deliveryType")}
              span={2}
            >
              {state.delivery_type === "storage" ? (
                <>
                  <Tag color="blue">
                    {t("bookingWizard.deliveryDetails.storage")}
                  </Tag>

                  {state.storage_option && isStorageOption(state.storage_option) && (
                    <Tag color="purple" style={{ marginLeft: 8 }}>
                      {t(STORAGE_OPTION_TRANSLATION_KEYS[state.storage_option])}
                    </Tag>
                  )}

                  <Typography.Text
                    type="secondary"
                    style={{ marginTop: 8, display: "block" }}
                  >
                    {t("bookingWizard.warehouseStorageMessage")}
                  </Typography.Text>
                </>
              ) : state.delivery_type === "address" ? (
                <Tag color="green">
                  {t("bookingWizard.deliveryDetails.address")}
                </Tag>
              ) : (
                <Tag color="default">{t("common.notSpecified")}</Tag>
              )}
            </Descriptions.Item>
            {state.delivery_type === "address" && state.delivery_details && (
              <>
                <Descriptions.Item
                  label={t("bookingWizard.deliveryDetails.streetName")}
                  span={2}
                >
                  <strong>
                    {state.delivery_details.street_name ||
                      t("common.notSpecified")}{" "}
                    {state.delivery_details.house_number || ""}
                  </strong>
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("bookingWizard.deliveryDetails.postalCode")}
                >
                  {state.delivery_details.postal_code ||
                    t("common.notSpecified")}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("bookingWizard.deliveryDetails.city")}
                >
                  {state.delivery_details.city || t("common.notSpecified")}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("bookingWizard.deliveryDetails.country")}
                  span={2}
                >
                  {state.delivery_details.country || t("common.notSpecified")}
                </Descriptions.Item>
                {state.delivery_details.instructions && (
                  <Descriptions.Item
                    label={t("bookingWizard.deliveryDetails.instructions")}
                    span={2}
                  >
                    <i>{state.delivery_details.instructions}</i>
                  </Descriptions.Item>
                )}
              </>
            )}
          </Descriptions>
        </Card>

        <Card title={t("bookingWizard.reviewDetails.productDetails")}>
          {state.products && state.products.length > 0 ? (
            <>
              <Table
                dataSource={state.products}
                pagination={false}
                rowKey={(record, index) =>
                  record.id?.toString() || `product-${index}`
                }
                bordered
              >
                <Table.Column title={t("products.name")} dataIndex="name" />
                <Table.Column
                  title={t("products.hsCode")}
                  dataIndex="hs_code"
                  render={(value) => value || t("common.na")}
                />
                <Table.Column
                  title={t("products.grossWeight")}
                  dataIndex="gross_weight"
                  render={(value) => (value ? `${value} kg` : t("common.na"))}
                />
                <Table.Column
                  title={t("products.cbm")}
                  dataIndex="cbm"
                  render={(value) =>
                    value ? `${value.toFixed(3)} m³` : t("common.na")
                  }
                />
                <Table.Column
                  title={t("products.value")}
                  dataIndex="value"
                  render={(value) =>
                    value ? `$${value.toFixed(2)}` : t("common.na")
                  }
                />
                <Table.Column
                  title={t("products.pieces")}
                  dataIndex="pieces"
                  render={(value) => value || t("common.na")}
                />
                <Table.Column
                  title={t("products.cartons")}
                  dataIndex="cartons"
                  render={(value) => value || t("common.na")}
                />
              </Table>

              <div style={{ marginTop: 16 }}>
                <Descriptions bordered column={2}>
                  <Descriptions.Item
                    label={t("bookingWizard.reviewDetails.totalValue")}
                  >
                    ${totals.totalValue.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("bookingWizard.reviewDetails.totalWeight")}
                  >
                    {totals.totalWeight} kg
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("bookingWizard.reviewDetails.totalVolume")}
                  >
                    {totals.totalCbm.toFixed(3)} m³
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("bookingWizard.reviewDetails.totalPieces")}
                  >
                    {totals.totalPieces}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("bookingWizard.reviewDetails.totalCartons")}
                  >
                    {totals.totalCartons}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </>
          ) : (
            <Empty description={t("common.noProducts")} />
          )}
        </Card>
      </Space>

      {/* Confirmation Modal */}
      <Modal
        title={t("bookingWizard.reviewDetails.confirmSubmitTitle")}
        open={isConfirmModalVisible}
        onOk={handleConfirmSubmit}
        onCancel={handleCancel}
        okText={t("bookingWizard.reviewDetails.confirmSubmit")}
        cancelText={t("common.cancel")}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text>
            {t("bookingWizard.reviewDetails.confirmSubmitMessage")}
          </Typography.Text>
          <Typography.Text type="secondary">
            {t("bookingWizard.reviewDetails.confirmSubmitInfo")}
          </Typography.Text>
        </Space>
      </Modal>

      {/* Add the loading modal */}
      <Modal
        title={t("bookingWizard.reviewDetails.processingBooking")}
        open={isGeneratingLabels}
        footer={null}
        closable={false}
        maskClosable={false}
        width={400}
      >
        <Space direction="vertical" style={{ width: "100%" }} align="center">
          <Spin size="large" />
          <Typography.Text>
            {t("bookingWizard.reviewDetails.processingBookingMessage")}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
            {t("bookingWizard.reviewDetails.processingBookingWarning")}
          </Typography.Text>
        </Space>
      </Modal>
    </WizardStep>
  );
};
